import React from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TablePagination from '@material-ui/core/TablePagination';
import TableRow from '@material-ui/core/TableRow';
import TableSortLabel from '@material-ui/core/TableSortLabel';
import Paper from '@material-ui/core/Paper';
import '../../../../css/programsList.css';
import {
  RenderSearchField,
  RenderAgesFilter,
  RenderSeasonFilter,
  RenderAll,
} from './listFunctions';

function desc(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

function stableSort(array, cmp) {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = cmp(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
}

function getSorting(order, orderBy) {
  return order === 'desc' ? (a, b) => desc(a, b, orderBy) : (a, b) => -desc(a, b, orderBy);
}

// collums titles
const headRows = [
  {
    id: 'program',
    numeric: false,
    disablePadding: true,
    label: 'Program Name',
  },
  {
    id: 'ages',
    numeric: false,
    disablePadding: true,
    label: 'Ages',
  },
  {
    id: 'season',
    numeric: false,
    disablePadding: false,
    label: 'Season',
  },
  {
    id: 'days',
    numeric: false,
    disablePadding: false,
    label: 'Days',
  },
  {
    id: 'time',
    numeric: false,
    disablePadding: false,
    label: 'Time',
  },
  {
    id: 'duration',
    numeric: false,
    disablePadding: false,
    label: 'Duration',
  },
  {
    id: 'spotsLeft',
    numeric: true,
    disablePadding: false,
    label: 'Spots Left',
  },
];

function EnhancedTableHead(props) {
  const { order, orderBy, onRequestSort } = props;
  const createSortHandler = (property) => (event) => {
    onRequestSort(event, property);
  };

  return (
    <TableHead className="bg-light">
      <TableRow>
        {headRows.map((row) => (
          <TableCell
            key={row.id}
            align={row.numeric ? 'center' : 'left'}
            padding={row.disablePadding ? 'none' : 'default'}
            sortDirection={orderBy === row.id ? order : false}
            className="table-cell"
          >
            <TableSortLabel
              active={orderBy === row.id}
              direction={order}
              onClick={createSortHandler(row.id)}
            >
              {row.label}
            </TableSortLabel>
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}

EnhancedTableHead.propTypes = {
  numSelected: PropTypes.number.isRequired,
  onRequestSort: PropTypes.func.isRequired,
  onSelectAllClick: PropTypes.func.isRequired,
  order: PropTypes.string.isRequired,
  orderBy: PropTypes.string.isRequired,
  rowCount: PropTypes.number.isRequired,
};

const useStyles = makeStyles((theme) => ({
  root: {
    width: '100%',
    marginTop: theme.spacing(1),
  },
  paper: {
    width: '100%',
    marginBottom: theme.spacing(2),
  },
  table: {
    minWidth: 750,
  },
  tableWrapper: {
    overflowX: 'auto',
  },
}));

export default function ProgramsTable({
  data,
  handleProgramSelection,
  sortSearch,
  sortAges,
  sortSeason,
}) {
  const classes = useStyles();
  const [order, setOrder] = React.useState('asc');
  const [orderBy, setOrderBy] = React.useState('season');
  const [selected, setSelected] = React.useState([]);
  const [page, setPage] = React.useState(0);
  const [dense] = React.useState(false);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);

  function handleRequestSort(event, property) {
    const isDesc = orderBy === property && order === 'desc';
    setOrder(isDesc ? 'asc' : 'desc');
    setOrderBy(property);
  }

  function handleSelectAllClick(event) {
    if (event.target.checked) {
      const newSelecteds = data.map((n) => n.name);
      setSelected(newSelecteds);
      return;
    }
    setSelected([]);
  }

  function handleChangePage(event, newPage) {
    setPage(newPage);
  }

  function handleChangeRowsPerPage(event) {
    setRowsPerPage(+event.target.value);
  }

  const filteredData = React.useMemo(() => {
    return data.filter((row) => {
      // 1. Search filter
      const searchMatch = !sortSearch || (row.name || '').toLowerCase().includes((sortSearch || '').toLowerCase());

      // 2. Age filter
      const ageArray = Array.isArray(sortAges) ? sortAges : (sortAges ? [sortAges] : []);
      const ageMatch = ageArray.length === 0 || ageArray.some((age) => (row.ages || '').toLowerCase() === (age || '').toLowerCase());

      // 3. Season filter
      const seasonArray = Array.isArray(sortSeason) ? sortSeason : (sortSeason ? [sortSeason] : []);
      const seasonMatch = seasonArray.length === 0 || (row.seasons && (
        Array.isArray(row.seasons)
          ? row.seasons.some((s) => seasonArray.some((filterSeason) => (s || '').toLowerCase().includes((filterSeason || '').toLowerCase())))
          : seasonArray.some((filterSeason) => (row.seasons || '').toLowerCase().includes((filterSeason || '').toLowerCase()))
      ));

      return searchMatch && ageMatch && seasonMatch;
    });
  }, [data, sortSearch, sortAges, sortSeason]);

  const emptyRows = rowsPerPage - Math.min(rowsPerPage, filteredData.length - page * rowsPerPage);
  let dataNumber = 0;
  return (
    <div className={classes.root}>
      <Paper className={classes.paper} elevation={0}>
        <div className={classes.tableWrapper}>
          <Table
            className={classes.table}
            aria-labelledby="tableTitle"
            size={dense ? 'small' : 'medium'}
          >
            <EnhancedTableHead
              numSelected={selected.length}
              order={order}
              orderBy={orderBy}
              onSelectAllClick={handleSelectAllClick}
              onRequestSort={handleRequestSort}
              rowCount={filteredData.length}
            />
            <TableBody>
              {stableSort(filteredData, getSorting(order, orderBy))
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((row, index) => {
                  dataNumber += 1;
                  return (
                    <RenderAll
                      data={row}
                      handleProgramSelection={handleProgramSelection}
                      key={row.programID}
                    />
                  );
                })}
              {emptyRows > 0 && <TableRow style={{ height: 49 * emptyRows }} />}
            </TableBody>
          </Table>
        </div>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={filteredData.length}
          rowsPerPage={rowsPerPage}
          page={page}
          backIconButtonProps={{
            'aria-label': 'Previous Page',
          }}
          nextIconButtonProps={{
            'aria-label': 'Next Page',
          }}
          onChangePage={handleChangePage}
          onChangeRowsPerPage={handleChangeRowsPerPage}
        />
      </Paper>
    </div>
  );
}

ProgramsTable.propTypes = {
  handleProgramSelection: PropTypes.func.isRequired,
  data: PropTypes.arrayOf(PropTypes.any).isRequired,
  sortSeason: PropTypes.arrayOf(PropTypes.string).isRequired,
  sortAges: PropTypes.arrayOf(PropTypes.string).isRequired,
  sortSearch: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)])
    .isRequired,
};
